import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { paymentsAPI } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PixelEvents } from "@/lib/pixel";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

const POLL_ATTEMPTS = 12;
const POLL_INTERVAL = 2000;

export default function PaymentResult({ cancelled = false }) {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState(cancelled ? "cancelled" : "polling");
    const [plan, setPlan] = useState(null);
    const navigate = useNavigate();
    const { lang } = useApp();

    useEffect(() => {
        if (cancelled || !sessionId) return;
        let cancel = false;
        let attempts = 0;
        const tick = async () => {
            if (cancel) return;
            try {
                const s = await paymentsAPI.status(sessionId);
                setPlan(s.plan);
                if (s.payment_status === "paid") {
                    setStatus("paid");
                    PixelEvents.purchase(s.plan || "unknown", s.amount_eur || 0, sessionId);
                    return;
                }
                if (s.status === "expired" || s.status === "failed") { setStatus("failed"); return; }
            } catch { /* ignore */ }
            attempts += 1;
            if (attempts >= POLL_ATTEMPTS) { setStatus("timeout"); return; }
            setTimeout(tick, POLL_INTERVAL);
        };
        tick();
        return () => { cancel = true; };
    }, [sessionId, cancelled]);

    const isPaid = status === "paid";
    const isFail = status === "failed" || status === "cancelled";
    const isPending = status === "polling" || status === "timeout";

    return (
        <div className="min-h-screen bg-[#080B10]">
            <Navbar />
            <div className="max-w-xl mx-auto px-4 py-24">
                <div className="rounded-lg border border-[#253046] bg-[#161D2A] p-10 text-center shadow-2xl">
                    {isPaid && (
                        <>
                            <div className="mx-auto w-16 h-16 rounded-full flame-gradient grid place-items-center flame-glow mb-6">
                                <CheckCircle2 className="w-8 h-8 text-black" strokeWidth={2.5} />
                            </div>
                            <div className="overline">Paiement confirmé</div>
                            <h1 className="font-display text-3xl font-black uppercase mt-3">
                                {lang === "fr" ? "Bienvenue à l'usine" : "Welcome to the factory"}
                            </h1>
                            <p className="text-slate-400 mt-4 text-sm">
                                {lang === "fr"
                                    ? `Ton plan ${plan?.toUpperCase() || ""} est actif. Ouvre le studio et lance ton premier épisode.`
                                    : `Your ${plan?.toUpperCase() || ""} plan is active. Head to the studio and generate your first episode.`}
                            </p>
                            <button
                                data-testid="payment-open-studio-btn"
                                onClick={() => navigate("/studio")}
                                className="mt-8 flame-gradient text-black font-semibold px-6 py-3 rounded flex items-center gap-2 mx-auto kinetic flame-glow"
                            >
                                {lang === "fr" ? "Ouvrir le studio" : "Open studio"} <ArrowRight className="w-4 h-4" />
                            </button>
                        </>
                    )}
                    {isPending && (
                        <>
                            <div className="mx-auto w-16 h-16 rounded-full bg-[#0F141C] border border-[#FF4D00]/40 grid place-items-center mb-6">
                                <Loader2 className="w-7 h-7 text-[#FF4D00] animate-spin" />
                            </div>
                            <div className="overline">
                                {status === "timeout" ? (lang === "fr" ? "Toujours en traitement" : "Still processing") : (lang === "fr" ? "Vérification" : "Verifying")}
                            </div>
                            <h1 className="font-display text-2xl font-black uppercase mt-3">
                                {lang === "fr" ? "On confirme ton paiement…" : "Confirming your payment…"}
                            </h1>
                            <p className="text-slate-400 mt-4 text-sm">
                                {lang === "fr" ? "Ça prend quelques secondes. Merci de patienter." : "This takes a few seconds. Please wait."}
                            </p>
                        </>
                    )}
                    {isFail && (
                        <>
                            <div className="mx-auto w-16 h-16 rounded-full bg-[#0F141C] border border-red-500/40 grid place-items-center mb-6">
                                <XCircle className="w-7 h-7 text-red-400" />
                            </div>
                            <div className="overline">{cancelled ? (lang === "fr" ? "Paiement annulé" : "Payment cancelled") : (lang === "fr" ? "Paiement échoué" : "Payment failed")}</div>
                            <h1 className="font-display text-2xl font-black uppercase mt-3">
                                {lang === "fr" ? "Aucun montant prélevé" : "No charge made"}
                            </h1>
                            <Link to="/" className="mt-6 inline-block text-[#FF4D00] hover:underline text-sm">← {lang === "fr" ? "Retour au site" : "Back to site"}</Link>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
